/**
 * Bundle Builder Utilities
 *
 * localStorage schema: ns_bundle_builder = {
 *   items: string[],
 *   timestamp: number
 * }
 *
 * Discount tiers:
 * - 2-3 domains: 5% off
 * - 4-5 domains: 10% off
 * - 6+ domains: 15% off
 */

import { fireEvent } from '@/lib/analytics';

const STORAGE_KEY = 'ns_bundle_builder';

interface BundleState {
  items: string[];
  timestamp: number;
}

// Discount configuration
const DISCOUNT_TIERS = [
  { min: 6, discount: 0.15, label: '15% off' },
  { min: 4, discount: 0.1, label: '10% off' },
  { min: 2, discount: 0.05, label: '5% off' },
  { min: 0, discount: 0.0, label: 'No discount' },
];

/**
 * Get current bundle state from localStorage
 */
export function getBundle(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const state: BundleState = JSON.parse(stored);

    // Expire bundles older than 7 days
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    if (state.timestamp < weekAgo) {
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }

    return state.items || [];
  } catch {
    return [];
  }
}

/**
 * Save bundle state to localStorage
 */
function saveBundle(items: string[]): void {
  try {
    const state: BundleState = {
      items: [...new Set(items)], // Dedupe
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save bundle to localStorage:', error);
  }
}

/**
 * Add domain to bundle
 */
export function addToBundle(domain: string): void {
  const current = getBundle();
  if (!current.includes(domain)) {
    const updated = [...current, domain];
    saveBundle(updated);

    // Analytics
    fireEvent('bundle_add', { domain });
  }
}

/**
 * Remove domain from bundle
 */
export function removeFromBundle(domain: string): void {
  const current = getBundle();
  const updated = current.filter((d) => d !== domain);

  if (updated.length !== current.length) {
    saveBundle(updated);

    // Analytics
    fireEvent('bundle_remove', { domain });
  }
}

/**
 * Clear entire bundle
 */
export function clearBundle(): void {
  const current = getBundle();
  if (current.length > 0) {
    localStorage.removeItem(STORAGE_KEY);

    // Analytics
    fireEvent('bundle_clear', { count: current.length });
  }
}

/**
 * Check if domain is in bundle
 */
export function hasInBundle(domain: string): boolean {
  return getBundle().includes(domain);
}

/**
 * Get bundle count
 */
export function getBundleCount(): number {
  return getBundle().length;
}

/**
 * Calculate discount tier for given count
 */
export function getDiscountTier(count: number): { discount: number; label: string } {
  const tier = DISCOUNT_TIERS.find((t) => count >= t.min);
  return tier || { discount: 0, label: 'No discount' };
}

/**
 * Calculate bundle pricing
 */
export function calculateBundlePrice(domains: Array<{ domain: string; bin_price?: number }>): {
  subtotal: number;
  discount: number;
  discountAmount: number;
  bundlePrice: number;
  discountLabel: string;
  pricedCount: number;
  noPriceCount: number;
} {
  // Only count domains with BIN prices
  const pricedDomains = domains.filter((d) => d.bin_price && d.bin_price > 0);
  const noPricedDomains = domains.filter((d) => !d.bin_price || d.bin_price <= 0);

  const subtotal = pricedDomains.reduce((sum, d) => sum + (d.bin_price || 0), 0);

  const tier = getDiscountTier(domains.length);
  const discountAmount = subtotal * tier.discount;
  const bundlePrice = subtotal - discountAmount;

  return {
    subtotal,
    discount: tier.discount,
    discountAmount,
    bundlePrice,
    discountLabel: tier.label,
    pricedCount: pricedDomains.length,
    noPriceCount: noPricedDomains.length,
  };
}

/**
 * Encode bundle items to URL query parameter
 */
export function encodeBundleToQuery(items: string[]): string {
  return items.join(',');
}

/**
 * Decode bundle items from URL query parameter
 */
export function decodeBundleFromQuery(queryString: string): string[] {
  if (!queryString) return [];
  return queryString
    .split(',')
    .filter(Boolean)
    .map((s) => s.trim());
}

/**
 * Merge items from URL with localStorage, removing duplicates
 */
export function mergeBundleFromQuery(queryItems: string[]): string[] {
  const currentItems = getBundle();
  const merged = [...new Set([...currentItems, ...queryItems])];

  if (merged.length !== currentItems.length) {
    saveBundle(merged);
  }

  return merged;
}
